import Vimeo from '@u-wave/react-vimeo';
import { useState, useEffect } from 'react'
import { VideoContainer, LoadingContainer, LoadingMessage } from './styles'
import VimeoErrorFallback from './components/VimeoErrorFallback'
import { useWindowSize } from "../../hooks/useWindowSize";
import VimeoErrorBoundary from './ErrorBoundary';

/**
 * Props del componente VimeoVideo
 */
interface ComponentProps {
  url: string;               // URL completa del video de Vimeo
  width?: number;            // Ancho del video (opcional)
  height?: number;           // Alto del video (opcional)
  showLink?: boolean;        // Mostrar enlace directo si no es embebible
  controls?: boolean;        // Mostrar controles del video
  isPreview?: boolean;       // Si es una vista previa (oculta enlaces)
  onOrientationDetected?: (isVertical: boolean) => void; // Callback para comunicar orientación
}


/**
 * Componente VimeoVideo - Renderiza videos de Vimeo con manejo de errores y fallbacks
 * 
 * @param controls - Mostrar controles del video (default: true)
 * @param url - URL completa del video de Vimeo
 * @param width - Ancho del video (opcional)
 * @param height - Alto del video (opcional)
 * @param showLink - Mostrar enlace directo si no es embebible (default: true)
 * @param isPreview - Si es una vista previa (default: false)
 */
const VimeoVideo = ({ controls = true, url, width, height, showLink = true, isPreview = false, onOrientationDetected }: ComponentProps) => {
  // En previews, cuando no hay permisos, mostrar el mensaje explicativo en lugar del botón
  const effectiveShowLink = isPreview ? false : showLink;

  // Estados del componente
  const [isVimeoEmbeddable, setIsVimeoEmbeddable] = useState<boolean | null>(null); // null = cargando, true = embebible, false = no embebible
  const [forceUpdate, setForceUpdate] = useState(0); // Para forzar re-render del componente Vimeo
  const [isVertical, setIsVertical] = useState(false); // Si el video es vertical (portrait)
  const [hasError, setHasError] = useState(false); // Si hubo un error al cargar
  const [isVideoLoaded, setIsVideoLoaded] = useState(false); // Si el video se cargó exitosamente
  const { isMobile } = useWindowSize(); // Hook para detectar si es móvil

  /**
   * Efecto que se ejecuta cuando cambia la URL
   * Verifica si el video de Vimeo es embebible y configura los estados correspondientes
   */
  useEffect(() => {
    console.log('🎬 VimeoVideo: Iniciando carga para URL:', url);
    setForceUpdate((prev) => prev + 1); // Forzar re-render del componente Vimeo
    setHasError(false); // Reset del estado de error
    setIsVideoLoaded(false); // Reset del estado de carga del video

    /**
     * Función asíncrona para verificar si el video es embebible
     * Hace una llamada a la API oembed de Vimeo
     */
    const checkEmbeddability = async () => {
      try {
        console.log('🎬 VimeoVideo: Haciendo llamada a la API oembed:', url);
        // Agregar timeout para evitar que se quede cargando indefinidamente
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 segundos timeout

        // Llamada a la API oembed de Vimeo usando la URL completa
        const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json'
          }
        });

        clearTimeout(timeoutId);

        // Verificar si la respuesta HTTP es exitosa
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Verificar si la respuesta contiene un challenge de seguridad de Vimeo
        if (data && typeof data === 'object' && data.html && data.html.includes('Verify to continue')) {
          throw new Error('Vimeo security challenge');
        }

        // Determinar si el video es vertical basado en las dimensiones
        if (!!data) {
          const newIsVertical = data.width < data.height;
          console.log('🎬 VimeoVideo: Datos recibidos de la API:', {
            width: data.width,
            height: data.height,
            isVertical: newIsVertical,
            hasCallback: !!onOrientationDetected
          });
          setIsVertical(newIsVertical);
          // Comunicar la orientación al componente padre
          onOrientationDetected?.(newIsVertical);
        }

        // Verifica si el video es embebible (si hay datos válidos)
        setIsVimeoEmbeddable(!!data);
      } catch (error) {
        // Maneja errores y establece a false
        setIsVimeoEmbeddable(false);
        setHasError(true);
      }
    };

    // Timeout de seguridad para evitar que se quede en loading indefinidamente
    // Solo aplicar timeout si el video no se ha cargado exitosamente
    const safetyTimeout = setTimeout(() => {
      if (isVimeoEmbeddable === null && !isVideoLoaded) {
        setIsVimeoEmbeddable(false);
        setHasError(true);
      }
    }, 15000); // 15 segundos timeout de seguridad (aumentado)

    // Solo verificar si hay una URL válida
    if (!!url) {
      checkEmbeddability();
    }

    // Cleanup: limpiar el timeout cuando el componente se desmonte o cambie la URL
    return () => {
      clearTimeout(safetyTimeout);
    };
  }, [url])

  /**
   * Manejador de errores para el componente Vimeo
   * Se ejecuta cuando hay un error al cargar el video embebido
   */
  const handleVimeoError = (error) => {
    // Solo marcar error si el video no se había cargado exitosamente
    if (!isVideoLoaded) {
      setHasError(true);
      setIsVimeoEmbeddable(false);
    }
  };

  /**
   * Manejador de carga exitosa del video
   * Se ejecuta cuando el video se carga correctamente
   */
  const handleVimeoLoad = () => {
    setIsVideoLoaded(true);
    setHasError(false);
  };

  /**
   * Props que se pasan al componente Vimeo de react-vimeo
   * Configuración básica del reproductor
   */
  const vimeoProps = {
    video: url, // Usar la URL completa del video
    color: "6c5ce7d9", // Color de acento de Vimeo
    showTitle: false, // No mostrar el título del video
  };

  // Estado de carga: mostrar loading mientras se verifica si el video es embebible
  if (isVimeoEmbeddable === null && !hasError) {
    return (
      <LoadingContainer>
        <LoadingMessage>Obteniendo Video de Vimeo</LoadingMessage>
      </LoadingContainer>
    )
  }

  // Estado de error o video no embebible: mostrar fallback con enlace directo
  // Solo mostrar error si no se ha cargado exitosamente el video
  if ((hasError || !isVimeoEmbeddable) && !isVideoLoaded) {
    return (
      <VimeoErrorFallback
        url={url}
        showLink={effectiveShowLink}
      />
    );
  }

  // Estado de éxito: renderizar el video embebido con error boundary
  return (
    <VimeoErrorBoundary
      onError={handleVimeoError}
      fallback={
        <VimeoErrorFallback
          url={url}
          showLink={effectiveShowLink}
        />
      }
    >
      <VideoContainer isMobile={isMobile} isVertical={isVertical} isPreview={isPreview} >
        <Vimeo
          {...vimeoProps}
          speed={true} // Habilitar controles de velocidad
          key={forceUpdate} // Forzar re-render cuando cambie el video
          responsive={true} // Hacer el video responsivo
          onError={handleVimeoError} // Manejador de errores del componente Vimeo
          onLoaded={handleVimeoLoad} // Manejador de carga exitosa del video
        />
      </VideoContainer>
    </VimeoErrorBoundary>
  );
};

export default VimeoVideo;
